const RECOVERY_KEY = "chunk_recovery_attempt";
const RECOVERY_WINDOW_MS = 60_000;

const isRecoverableChunkError = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return (
    normalized.includes("chunkloaderror") ||
    normalized.includes("loading chunk") ||
    normalized.includes("failed to fetch dynamically imported module") ||
    normalized.includes("unexpected token '<'")
  );
};

const shouldAttemptRecovery = (): boolean => {
  try {
    const raw = sessionStorage.getItem(RECOVERY_KEY);
    const now = Date.now();
    if (!raw) {
      sessionStorage.setItem(RECOVERY_KEY, String(now));
      return true;
    }
    const previous = Number(raw);
    if (Number.isNaN(previous) || now - previous > RECOVERY_WINDOW_MS) {
      sessionStorage.setItem(RECOVERY_KEY, String(now));
      return true;
    }
    return false;
  } catch {
    // If storage is blocked, still try once.
    return true;
  }
};

const recoverFromChunkError = () => {
  if (!shouldAttemptRecovery()) return;
  const url = new URL(window.location.href);
  url.searchParams.set("_r", String(Date.now()));
  window.location.replace(url.toString());
};

export const initChunkLoadRecovery = () => {
  window.addEventListener("error", (event: ErrorEvent) => {
    const scriptSrc =
      (event.target as HTMLScriptElement | null)?.src ||
      (event.currentTarget as HTMLScriptElement | null)?.src ||
      "";
    const message = String(event.message || "");

    // Script file failed or HTML returned for JS chunk.
    if (scriptSrc.includes("/static/js/") || isRecoverableChunkError(message)) {
      recoverFromChunkError();
    }
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message =
      typeof reason === "string"
        ? reason
        : String(reason?.message || reason || "");
    if (isRecoverableChunkError(message)) {
      recoverFromChunkError();
    }
  });
};

const CHUNK_RELOAD_GUARD_KEY = 'sx_chunk_reload_guard_ts';
const CHUNK_RELOAD_GUARD_WINDOW_MS = 60 * 1000;

const hasWindow = () => typeof window !== 'undefined';

const now = () => Date.now();

const hasRecentReloadAttempt = (): boolean => {
  if (!hasWindow()) return false;
  const raw = window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
  if (!raw) return false;

  const timestamp = Number(raw);
  if (!Number.isFinite(timestamp)) return false;
  return now() - timestamp < CHUNK_RELOAD_GUARD_WINDOW_MS;
};

const markReloadAttempt = (): void => {
  if (!hasWindow()) return;
  window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(now()));
};

const clearExpiredGuard = (): void => {
  if (!hasWindow()) return;
  const raw = window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
  if (!raw) return;
  const timestamp = Number(raw);
  if (!Number.isFinite(timestamp) || now() - timestamp > CHUNK_RELOAD_GUARD_WINDOW_MS) {
    window.sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
  }
};

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;

  const err = error as {
    name?: string;
    message?: string;
    type?: string;
    reason?: { name?: string; message?: string };
  };

  const name = `${err.name ?? err.reason?.name ?? ''}`.toLowerCase();
  const message = `${err.message ?? err.reason?.message ?? ''}`.toLowerCase();

  return (
    name.includes('chunkloaderror') ||
    message.includes('loading chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed')
  );
};

export const showRefreshingMessage = (): void => {
  if (!hasWindow()) return;
  if (document.getElementById('chunk-refresh-banner')) return;

  const banner = document.createElement('div');
  banner.id = 'chunk-refresh-banner';
  banner.textContent = 'A new version is available. Refreshing...';
  banner.style.position = 'fixed';
  banner.style.top = '16px';
  banner.style.right = '16px';
  banner.style.padding = '10px 14px';
  banner.style.zIndex = '9999';
  banner.style.borderRadius = '10px';
  banner.style.background = '#111827';
  banner.style.color = '#fff';
  banner.style.fontFamily = 'system-ui, sans-serif';
  banner.style.fontSize = '13px';
  banner.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
  document.body.appendChild(banner);
};

/**
 * Reloads the page at most once in a short time window.
 * This prevents infinite reload loops if the issue persists.
 */
export const reloadOnceForChunkError = (): boolean => {
  if (!hasWindow()) return false;
  clearExpiredGuard();

  if (hasRecentReloadAttempt()) {
    return false;
  }

  markReloadAttempt();
  showRefreshingMessage();
  window.location.reload();
  return true;
};

/**
 * Global handlers catch chunk load errors thrown outside React boundaries
 * (script loading errors and dynamic import rejections).
 */
export const setupGlobalChunkErrorHandler = (): void => {
  if (!hasWindow()) return;

  clearExpiredGuard();

  window.addEventListener('error', (event: ErrorEvent) => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
      reloadOnceForChunkError();
    }
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      reloadOnceForChunkError();
    }
  });
};
